import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { useParams, useLocation } from 'react-router-dom'
import { useConnectionStore } from '@/stores/connectionStore'
import { useCollectionsStore } from '@/stores/collectionsStore'
import { useUIStore } from '@/stores/uiStore'
import { TabbedSqlEditor, type EditorTab, type TabbedSqlEditorRef } from '@/components/query/TabbedSqlEditor'
import { QueryResults } from '@/components/query/QueryResults'
import { SavedQueriesWithCollections } from '@/components/query/SavedQueriesWithCollections'
import { AIAssistant } from '@/components/query/AIAssistant'
import { CollectionManager } from '@/components/common/CollectionManager'
import { VerticalToolbar } from '@/components/query/VerticalToolbar'
import { PaginatedSelect } from '@/components/common/PaginatedSelect'
import { Allotment } from 'allotment'
import { COLLECTION_EVENTS, dispatchCollectionEvent } from '@/utils/collectionEvents'
import {
  PlayIcon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { format } from 'sql-formatter'
import { toast } from 'react-hot-toast'
import { 
  useExecuteQueryMutation, 
  useQueryHistoryQuery, 
  useExplainQueryMutation 
} from '@/hooks/useQuery'
import { useDatabasesQuery, useSchemasInfiniteQuery } from '@/hooks/useDatabase'
import { fetchDataSourceDirect } from '@/stores/dataSourceStore'
import { isDatabaseHidden, isSchemaHidden } from '@/utils/databaseFilterStorage'
import type { CollectionQuery } from '@/services/api/models/CollectionQuery'
import type { AgentProxyExecuteQueryResponse } from '@/services/api/models/AgentProxyExecuteQueryResponse'
import { 
  saveQueryState, 
  loadQueryState, 
  saveQueryText, 
  saveQueryResults,
  cleanupOldStates,
  saveTabs
} from '@/utils/queryStorage'
import { parseQueries, stripComments } from '@/utils/sqlParser'



interface ExtendedQueryResult {
  columns: string[]
  rows: unknown[][]
  rowCount: number
  executionTime: number
  executionTimeMs?: number
  status?: 'success' | 'error' | 'pending'
  message?: string
  query: string
  timestamp: Date
  affectedRows?: number
  savedQueryId?: string
  savedQueryName?: string
  databaseContext?: {
    database?: string
    schema?: string
  }
}

export function QueryEditor() {
  const { connectionId } = useParams<{ connectionId: string }>()
  const location = useLocation()
  const { connections, activeConnection, setActiveConnection } = useConnectionStore()

  const [sqlQuery, setSqlQuery] = useState('')
  const [queryResults, setQueryResults] = useState<ExtendedQueryResult[]>([])
  const [activeResultTab, setActiveResultTab] = useState(0)
  // Use global UI store for panel states
  const { ui, setShowAIAssistant, setShowQueryLibrary, setShowQueryHistory, setRightSidebarWidth } = useUIStore()
  const { showAIAssistant, showQueryLibrary, showQueryHistory, rightSidebarWidth } = ui
  
  // Preserve existing variable names for compatibility
  const showHistory = showQueryHistory
  const showSavedQueries = showQueryLibrary
  const [shouldAutoExecute, setShouldAutoExecute] = useState(false)
  const [editorSelection] = useState<{ startLine: number; startColumn: number; endLine: number; endColumn: number } | null>(null)
  const [pendingSchemaRestore, setPendingSchemaRestore] = useState<string | null>(null)
  const [queryFromUrl, setQueryFromUrl] = useState<string | null>(null)
  const [shouldCreateNewTab, setShouldCreateNewTab] = useState<boolean>(false)
  const [urlParamsProcessed, setUrlParamsProcessed] = useState<boolean>(false)
  const [databaseFromUrl, setDatabaseFromUrl] = useState<string | null>(null)
  const [schemaFromUrl, setSchemaFromUrl] = useState<string | null>(null)
  const [pendingQueryLoad, setPendingQueryLoad] = useState<string | null>(null)
  const [collectionManagerOpen, setCollectionManagerOpen] = useState(false)
  const [collectionManagerMode, setCollectionManagerMode] = useState<'create-query' | 'create-collection' | 'edit-collection'>('create-query')
  const [editingTab, setEditingTab] = useState<EditorTab | null>(null)
  const [historyFilter, setHistoryFilter] = useState<'all' | 'success' | 'error'>('all')
  // rightSidebarWidth is now managed by the UI store
  
  // Track current tabs state for save button
  const [currentTabs, setCurrentTabs] = useState<EditorTab[]>([])
  const [activeTabId, setActiveTabId] = useState<string>('')
  
  // Ref for TabbedSqlEditor to access its methods
  const tabbedSqlEditorRef = useRef<TabbedSqlEditorRef>(null)
  
  // Stable callback references for PaginatedSelect components
  const getDatabaseOptionValue = useCallback((db: { name?: string }) => db.name || '', [])
  const getDatabaseOptionLabel = useCallback((db: { name?: string }) => db.name || '', [])
  const getSchemaOptionValue = useCallback((schema: { name?: string }) => schema.name || '', [])
  const getSchemaOptionLabel = useCallback((schema: { name?: string }) => schema.name || '', [])
  
  // Stable arrays for Allotment defaultSizes
  const allotmentMainSizes = useMemo(() => [100 - rightSidebarWidth, rightSidebarWidth], [rightSidebarWidth])
  const allotmentVerticalSizes = useMemo(() => [400, 600], [])
  
  // Calculate dynamic max size for right sidebar (50% of viewport width)
  const [viewportWidth, setViewportWidth] = useState(window.innerWidth)
  
  useEffect(() => {
    const handleResize = () => {
      setViewportWidth(window.innerWidth)
    }
    
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])
  
  // Calculate min and max sizes as percentages of viewport width
  const rightSidebarMinSize = useMemo(() => Math.max(200, Math.floor(viewportWidth * 0.2)), [viewportWidth]) // 20% or 200px minimum
  const rightSidebarMaxSize = useMemo(() => Math.floor(viewportWidth * 0.5), [viewportWidth]) // 50% maximum
  
  
  // Parse SQL queries separated by semicolon with PostgreSQL dollar-quote support
  
  // Get the query to execute based on cursor position or selection
  const getQueryToExecute = useCallback(() => {
    const queries = parseQueries(sqlQuery)
    
    if (queries.length === 0) return sqlQuery.trim()
    if (queries.length === 1) return queries[0].query
    
    // If there's a selection, find the query that contains it
    if (editorSelection) {
      const { startLine, startColumn } = editorSelection
      
      for (const query of queries) {
        if (startLine >= query.startLine && startLine <= query.endLine) {
          if (startLine === query.startLine && startColumn < query.startColumn) continue
          if (startLine === query.endLine && startColumn > query.endColumn) continue
          return query.query
        }
      }
    }
    
    // If no selection or selection doesn't match any query, return the first query
    return queries[0].query
  }, [sqlQuery, editorSelection])
  
  // Format execution time with proper units and precision
  const formatExecutionTime = (executionTimeMs?: number, fallbackExecutionTime?: number): string => {
    const timeMs = executionTimeMs ?? (fallbackExecutionTime ? fallbackExecutionTime * 1000 : 0);
    
    if (timeMs === 0) return '< 1ms';
    
    if (timeMs < 1000) {
      return `${Math.round(timeMs)}ms`;
    } else if (timeMs < 60000) {
      return `${(timeMs / 1000).toFixed(2)}s`;
    } else {
      const mins = Math.floor(timeMs / 60000);
      const secs = ((timeMs % 60000) / 1000).toFixed(2);
      return `${mins}m ${secs}s`;
    }
  }
  
  // Database/Schema selection state
  const [selectedDatabase, setSelectedDatabase] = useState<string>('')
  const [selectedSchema, setSelectedSchema] = useState<string>('public') // Default for PostgreSQL
  const [hasUserSelectedSchema, setHasUserSelectedSchema] = useState<boolean>(false) // Track if user manually selected schema
  const [isRestoringState, setIsRestoringState] = useState<boolean>(false) // Track state restoration

  // Stable database context object
  const databaseContext = useMemo(() => ({
    connectionType: activeConnection?.type || 'mysql',
    database: selectedDatabase,
    schema: selectedSchema
  }), [activeConnection?.type, selectedDatabase, selectedSchema])
  
  // Stable saved query info object
  const savedQueryInfo = useMemo(() => editingTab ? {
    savedQueryId: editingTab.savedQueryId,
    savedQueryCollectionId: editingTab.savedQueryCollectionId,
    savedQueryName: editingTab.savedQueryName,
    databaseName: editingTab.databaseName,
    schemaName: editingTab.schemaName
  } : undefined, [editingTab])
  
  // Stable initial query object for collection manager
  const initialQuery = useMemo(() => 
    editingTab && collectionManagerMode === 'create-query' ? {
      query: editingTab.content,
      name: editingTab.label,
      databaseName: selectedDatabase,
      schemaName: activeConnection?.type === 'postgresql' ? selectedSchema : undefined
    } : undefined, [editingTab, collectionManagerMode, selectedDatabase, activeConnection?.type, selectedSchema])

  // Use the databases query hook with infinite scroll pagination
  // In Query Editor, avoid enriching database list with per-database details to prevent
  // access denied errors for read-only MySQL users on system databases.
  const databasesQuery = useDatabasesQuery(activeConnection?.id || '', { enrichDetails: false })

  // Use the schemas query hook with infinite scroll pagination
  const schemasQuery = useSchemasInfiniteQuery(
    activeConnection?.id || '',
    selectedDatabase,
    { enabled: !!activeConnection && !!selectedDatabase && activeConnection.type === 'postgresql' }
  )

  // Calculate filtered databases and schemas for the dropdowns
  const filteredDatabases = useMemo(() => {
    if (!activeConnection) return []
    const dbType = activeConnection.type === 'postgresql' ? 'postgresql' : 'mysql'
    return databasesQuery.data
      .filter((db): db is typeof db & { name: string } => !!db.name)
      .filter(db => !isDatabaseHidden(activeConnection.id, db.name, dbType))
  }, [databasesQuery.data, activeConnection])

  const filteredSchemas = useMemo(() => {
    if (!activeConnection || !selectedDatabase) return []
    const dbType = activeConnection.type === 'postgresql' ? 'postgresql' : 'mysql'
    return schemasQuery.data
      .filter((schema): schema is typeof schema & { name: string } => !!schema.name)
      .filter(schema => !isSchemaHidden(activeConnection.id, selectedDatabase, schema.name, dbType))
  }, [schemasQuery.data, activeConnection, selectedDatabase])

  // Calculate dynamic widths based on content length
  const getDynamicDatabaseWidth = useMemo(() => {
    if (filteredDatabases.length === 0) return 'min-w-24' // Default minimum
    
    const maxLength = Math.max(
      ...filteredDatabases.map(db => (db.name || '').length),
      8 // Minimum reasonable width
    )
    
    // More accurate calculation based on character width in small text
    if (maxLength <= 8) return 'min-w-24'  // 6rem - for "postgres" etc
    if (maxLength <= 12) return 'min-w-32' // 8rem - for medium names
    if (maxLength <= 16) return 'min-w-40' // 10rem - for longer names
    if (maxLength <= 20) return 'min-w-48' // 12rem - for very long names
    return 'min-w-56' // 14rem - maximum reasonable width
  }, [filteredDatabases])
  
  const getDynamicSchemaWidth = useMemo(() => {
    if (filteredSchemas.length === 0) return 'min-w-24' // Default minimum
    
    const maxLength = Math.max(
      ...filteredSchemas.map(schema => (schema.name || '').length),
      6 // Minimum reasonable width
    )
    
    // More accurate calculation based on character width in small text
    if (maxLength <= 8) return 'min-w-24'  // 6rem - for "public" etc
    if (maxLength <= 12) return 'min-w-32' // 8rem - for medium names
    if (maxLength <= 16) return 'min-w-40' // 10rem - for longer names
    if (maxLength <= 20) return 'min-w-48' // 12rem - for very long names
    return 'min-w-56' // 14rem - maximum reasonable width
  }, [filteredSchemas])

  // Auto-select first database if available and none is selected
  useEffect(() => {
    if (databasesQuery.data.length > 0 && !selectedDatabase && !databaseFromUrl) {
      const [firstDb] = databasesQuery.data
      if (firstDb.name) {
        setSelectedDatabase(firstDb.name)
      }
    }
  }, [databasesQuery.data, selectedDatabase, urlParamsProcessed, databaseFromUrl])

  // Apply database from URL after databases are loaded
  useEffect(() => {
    if (databaseFromUrl && databasesQuery.data.length > 0) {
      console.log('[URL Database Apply] Applying database from URL:', databaseFromUrl)
      console.log('[URL Database Apply] Available databases:', databasesQuery.data.map(db => db.name))
      
      // Check if the database exists in the list
      const dbExists = databasesQuery.data.some(db => db.name === databaseFromUrl)
      if (dbExists) {
        console.log('[URL Database Apply] Database exists, setting it:', databaseFromUrl)
        setSelectedDatabase(databaseFromUrl)
      } else {
        console.log('[URL Database Apply] Database not found in list:', databaseFromUrl)
        toast.error(`Database "${databaseFromUrl}" not found in this connection`)
      }
      
      // Clear the URL database to prevent re-applying
      setDatabaseFromUrl(null)
    }
  }, [databaseFromUrl, databasesQuery.data])

  // Apply schema from URL after schemas are loaded
  useEffect(() => {
    if (schemaFromUrl && schemasQuery.data.length > 0 && activeConnection && activeConnection.type === 'postgresql') {
      console.log('[URL Schema Apply] Applying schema from URL:', schemaFromUrl)
      setSelectedSchema(schemaFromUrl)
      setHasUserSelectedSchema(true)
      setSchemaFromUrl(null)
    }
  }, [schemaFromUrl, schemasQuery.data, activeConnection])

  // Auto-select schema when schemas are loaded (only if user hasn't manually selected one)
  useEffect(() => {
    if (schemasQuery.data.length > 0 && activeConnection && activeConnection.type === 'postgresql' && !hasUserSelectedSchema) {
      // Auto-select 'public' schema if available, otherwise first schema
      const publicSchema = schemasQuery.data.find(s => s.name === 'public')
      if (publicSchema && selectedSchema !== 'public') {
        console.log('Auto-selecting public schema')
        setSelectedSchema('public')
      } else if (!publicSchema && schemasQuery.data[0].name && selectedSchema !== schemasQuery.data[0].name) {
        console.log('Auto-selecting first schema:', schemasQuery.data[0].name)
        setSelectedSchema(schemasQuery.data[0].name)
      }
    }
  }, [schemasQuery.data, activeConnection, selectedSchema, hasUserSelectedSchema])

  // Handler for manual schema selection
  const handleSchemaChange = useCallback((newSchema: string) => {
    console.log('User manually selected schema:', newSchema)
    setSelectedSchema(newSchema)
    setHasUserSelectedSchema(true)
  }, [])

  // Handle pending schema restoration when schemas are loaded
  useEffect(() => {
    if (pendingSchemaRestore && schemasQuery.data.length > 0 && activeConnection && activeConnection.type === 'postgresql') {
      const isSchemaAvailable = schemasQuery.data.some(schema => schema.name === pendingSchemaRestore)
      if (isSchemaAvailable) {
        console.log('Restoring pending schema:', pendingSchemaRestore)
        setSelectedSchema(pendingSchemaRestore)
        setHasUserSelectedSchema(true)
        // Don't show additional toast here, the main success message already shows schema info
      } else {
        toast.error(`Schema "${pendingSchemaRestore}" from saved query is not available in current database`)
      }
      setPendingSchemaRestore(null) // Clear pending restoration
    }
  }, [schemasQuery.data, pendingSchemaRestore, activeConnection])

  // Clear pending schema restoration when database changes (unless it's part of restoration)
  useEffect(() => {
    // Only clear if schemas query is enabled and we're not in the middle of a database restoration
    if (schemasQuery.isLoading && pendingSchemaRestore) {
      console.log('Database changed, schemas are loading for pending restoration')
    } else if (!schemasQuery.isLoading && schemasQuery.data.length === 0 && pendingSchemaRestore) {
      console.log('No schemas available, clearing pending restoration')
      setPendingSchemaRestore(null)
    }
  }, [selectedDatabase, schemasQuery.isLoading, schemasQuery.data.length, pendingSchemaRestore])

  const executeQueryMutation = useExecuteQueryMutation()
  const { refetch: refetchHistory } = useQueryHistoryQuery()
  const explainQueryMutation = useExplainQueryMutation()
  const { fetchCollections } = useCollectionsStore()

  // Fetch collections when component mounts
  useEffect(() => {
    void fetchCollections()
  }, [fetchCollections])

  // Execute a specific query (used for bulk execution)
  const executeSpecificQuery = useCallback(async (queryToExecute: string) => {
    if (!queryToExecute.trim()) return
    if (!activeConnection || !selectedDatabase) return

    try {
      const queryRequest = {
        datasourceId: activeConnection.id,
        databaseName: selectedDatabase,
        query: queryToExecute.trim(),
        limit: 1000,
        timeout: 30,
        schema: activeConnection.type === 'postgresql' ? selectedSchema : undefined,
        connectionType: activeConnection.type,
      }

      const result = await executeQueryMutation.mutateAsync(queryRequest)

      // Transform API result to match UI expectations
      // Handle the AgentProxyExecuteQueryResponse type
      const resultData = result as AgentProxyExecuteQueryResponse;
      const extendedResult: ExtendedQueryResult = {
        columns: Array.isArray(resultData.columns) 
          ? resultData.columns.map((col: string | { name: string }) => {
              if (typeof col === 'string') return col;
              if (typeof col === 'object' && 'name' in col) {
                return col.name;
              }
              return String(col);
            })
          : [],
        rows: Array.isArray(resultData.rows) 
          ? resultData.rows.map((row: unknown[] | Record<string, unknown>) => Array.isArray(row) ? row : Object.values(row))
          : [],
        rowCount: Array.isArray(resultData.rows) ? resultData.rows.length : (resultData.totalRows || 0),
        executionTime: resultData.execution_time_ms ? resultData.execution_time_ms / 1000 : 0,
        executionTimeMs: resultData.execution_time_ms || 0,
        affectedRows: resultData.affected_rows || 0,
        query: queryToExecute,
        timestamp: new Date(),
        status: 'success' as const,
        message: resultData.message,
      }

      // Check if we're executing from a pending tab
      const currentTab = queryResults[activeResultTab]
      let newResults: ExtendedQueryResult[]
      
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (currentTab && currentTab.status === 'pending' && currentTab.query === queryToExecute) {
        // Update the existing pending tab
        newResults = Array.from(queryResults)
        newResults[activeResultTab] = {
          ...extendedResult,
          savedQueryId: currentTab.savedQueryId,
          savedQueryName: currentTab.savedQueryName,
        }
      } else {
        // Create a new tab for the result
        newResults = [extendedResult, ...queryResults.slice(0, 49)]
        setActiveResultTab(0)
      }
      
      setQueryResults(newResults)
      
      // Save results to localStorage
      const persistedResults = newResults.slice(0, 50)
        .filter(result => result.status !== 'pending')
        .map(result => ({
          ...result,
          timestamp: result.timestamp.toISOString(),
          status: result.status as 'success' | 'error' | undefined
        }))
      saveQueryResults(
        activeConnection.id,
        persistedResults,
        queryToExecute,
        selectedDatabase,
        selectedSchema
      )
      
      void refetchHistory()
    } catch (error: unknown) {
      console.error('Query execution error:', error)
      
      // Handle specific validation errors
      let errorMessage = 'Query execution failed'
      
      if (error && typeof error === 'object' && 'status' in error && error.status === 400) {
        if ('body' in error && error.body && typeof error.body === 'object' && 'message' in error.body) {
          const { body } = error as { body: { message: string } }
          errorMessage = `Validation Error: ${body.message}`
        } else if ('body' in error && error.body && typeof error.body === 'object' && 'errors' in error.body) {
          const { body } = error as { body: { errors: unknown } }
          const { errors } = body
          const validationErrors = Array.isArray(errors) 
            ? errors.join(', ')
            : JSON.stringify(errors)
          errorMessage = `Validation Error: ${validationErrors}`
        } else {
          errorMessage = 'Bad Request: Please check your query and connection settings'
        }
      } else if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        errorMessage = 'Authentication failed. Please check your login credentials.'
      } else if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        errorMessage = 'Data source or database not found. Please check your connection settings.'
      } else if (error && typeof error === 'object' && 'status' in error && error.status === 500) {
        if ('body' in error && error.body && typeof error.body === 'object' && 'message' in error.body) {
          const { body } = error as { body: { message: string } }
          errorMessage = body.message
        } else {
          errorMessage = 'Server error. Please try again later.'
        }
      } else if (error instanceof Error) {
        errorMessage = error.message
      }
      
      const failedResult: ExtendedQueryResult = {
        columns: [],
        rows: [],
        rowCount: 0,
        executionTime: 0,
        executionTimeMs: 0,
        status: 'error',
        message: errorMessage,
        query: queryToExecute,
        timestamp: new Date(),
        affectedRows: undefined,
      }
      
      // Check if we're executing from a pending tab
      const currentTab = queryResults[activeResultTab]
      let newResults: ExtendedQueryResult[]
      
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (currentTab && currentTab.status === 'pending' && currentTab.query === queryToExecute) {
        // Update the existing pending tab with error
        newResults = Array.from(queryResults)
        newResults[activeResultTab] = {
          ...failedResult,
          savedQueryId: currentTab.savedQueryId,
          savedQueryName: currentTab.savedQueryName,
        }
      } else {
        // Create a new tab for the error
        newResults = [failedResult, ...queryResults.slice(0, 49)]
        setActiveResultTab(0)
      }
      
      setQueryResults(newResults)
      
      // Save error results to localStorage
      const persistedResults = newResults.slice(0, 50)
        .filter(result => result.status !== 'pending')
        .map(result => ({
          ...result,
          timestamp: result.timestamp.toISOString(),
          status: result.status as 'success' | 'error' | undefined
        }))
      saveQueryResults(
        activeConnection.id,
        persistedResults,
        queryToExecute,
        selectedDatabase,
        selectedSchema
      )
      
      throw error // Re-throw to stop execution of remaining queries
    }
  }, [activeConnection, selectedDatabase, executeQueryMutation, refetchHistory, activeResultTab, queryResults, selectedSchema])

  useEffect(() => {
    
    if (connectionId && !activeConnection) {
      const connection = connections.find(conn => conn.id === connectionId)
      if (connection) {
        setActiveConnection(connection)
      } else {
        console.log('Connection not found in connections list')
      }
    }


  }, [connectionId, activeConnection, connections, setActiveConnection])

  // Handle panel resize
  const handlePanelResize = useCallback((sizes: number[]) => {
    // Only save when we have 2 panes (main content + sidebar)
    if (sizes.length === 2 && (showSavedQueries || showHistory || showAIAssistant)) {
      // Calculate the percentage width of the right sidebar
      const totalWidth = sizes[0] + sizes[1]
      const rightPanelPercentage = (sizes[1] / totalWidth) * 100
      
      // Save the size to global store
      setRightSidebarWidth(rightPanelPercentage)
    }
  }, [showSavedQueries, showHistory, showAIAssistant, setRightSidebarWidth])

  // Query Library visibility is now persisted automatically by the UI store

  // Restore query state when active connection changes
  useEffect(() => {
    if (activeConnection && !isRestoringState) {
      setIsRestoringState(true)
      
      try {
        const storedState = loadQueryState(activeConnection.id)
        if (storedState) {
          // console.log('Found stored state:', storedState)
          
          // Restore SQL query
          if (storedState.sqlQuery && storedState.sqlQuery.trim() !== '') {
            setSqlQuery(storedState.sqlQuery)
          }
          
          // Restore database selection (only if no URL params were processed)
          if (storedState.selectedDatabase && !urlParamsProcessed && !databaseFromUrl) {
            setSelectedDatabase(storedState.selectedDatabase)
          }
          
          // Restore schema selection (only if no URL params were processed and PostgreSQL)
          if (storedState.selectedSchema && activeConnection.type === 'postgresql' && !urlParamsProcessed && !schemaFromUrl) {
            setSelectedSchema(storedState.selectedSchema)
            setHasUserSelectedSchema(true)
          }
          
          // Restore query results with proper type conversion
          if (storedState.queryResults.length > 0) {
            const convertedResults: ExtendedQueryResult[] = storedState.queryResults.map(result => ({
              ...result,
              timestamp: new Date(result.timestamp)
            }))
            setQueryResults(convertedResults)
            setActiveResultTab(0)
          }
        }
      } catch (error) {
        console.error('Error restoring query state:', error)
      } finally {
        setIsRestoringState(false)
      }
    }
  }, [activeConnection, urlParamsProcessed, databaseFromUrl, schemaFromUrl, isRestoringState])

  // Cleanup old states when connections are available
  useEffect(() => {
    if (connections.length > 0) {
      const validDatasourceIds = connections.map(conn => conn.id)
      cleanupOldStates(validDatasourceIds)
    }
  }, [connections])

  // Save query text to localStorage when it changes (debounced)
  useEffect(() => {
    if (activeConnection && sqlQuery && !isRestoringState) {
      const timeoutId = setTimeout(() => {
        saveQueryText(activeConnection.id, sqlQuery)
      }, 500) // Debounce to avoid excessive localStorage writes
      
      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [sqlQuery, activeConnection, isRestoringState])

  // Save state when database or schema changes
  useEffect(() => {
    if (activeConnection && selectedDatabase && !isRestoringState) {
      const persistedResults = queryResults
        .filter(result => result.status !== 'pending')
        .map(result => ({
          ...result,
          timestamp: result.timestamp.toISOString(),
          status: result.status as 'success' | 'error' | undefined
        }))
      saveQueryState(
        activeConnection.id,
        sqlQuery,
        selectedDatabase,
        selectedSchema,
        persistedResults
      )
    }
  }, [selectedDatabase, selectedSchema, activeConnection, sqlQuery, queryResults, isRestoringState])

  // Set default query when database or schema changes (only if no stored state)
  useEffect(() => {
    if (!activeConnection || !selectedDatabase || isRestoringState) return
    
    let defaultQuery
    if (activeConnection.type === 'postgresql') {
      defaultQuery = selectedSchema 
        ? `-- Show tables in ${selectedDatabase}.${selectedSchema}\nSELECT table_name FROM information_schema.tables WHERE table_schema = '${selectedSchema}';`
        : `-- Show tables in ${selectedDatabase}\nSELECT table_name FROM information_schema.tables WHERE table_schema = 'public';`
    } else {
      defaultQuery = `-- Show tables in ${selectedDatabase}\nSHOW TABLES;`
    }
    
    // Only set default query if there's no current query and no stored state
    if (!sqlQuery.trim()) {
      const storedState = loadQueryState(activeConnection.id)
      if (!storedState || !storedState.sqlQuery.trim()) {
        setSqlQuery(defaultQuery)
      }
    }
  }, [activeConnection, selectedDatabase, selectedSchema, sqlQuery, isRestoringState])

  // Handle URL parameters for pre-filled queries and auto-execution
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)
    const queryParam = searchParams.get('query')
    const databaseParam = searchParams.get('database')
    const schemaParam = searchParams.get('schema')
    const autoExecuteParam = searchParams.get('autoExecute')
    const newTabParam = searchParams.get('newTab')

    if (queryParam) {
      try {
        const decodedQuery = decodeURIComponent(queryParam)
        setSqlQuery(decodedQuery)
        setQueryFromUrl(decodedQuery) // Track that this came from URL
      } catch (error) {
        console.error('Error decoding query parameter:', error)
      }
    }

    if (databaseParam) {
      try {
        const decodedDatabase = decodeURIComponent(databaseParam)
        console.log('[URL Params] Setting database from URL:', decodedDatabase)
        setDatabaseFromUrl(decodedDatabase)
        setSelectedDatabase(decodedDatabase)
      } catch (error) {
        console.error('Error decoding database parameter:', error)
      }
    }

    if (schemaParam) {
      try {
        const decodedSchema = decodeURIComponent(schemaParam)
        setSchemaFromUrl(decodedSchema)
        setSelectedSchema(decodedSchema)
        setHasUserSelectedSchema(true) // Mark as user-selected to prevent auto-selection override
      } catch (error) {
        console.error('Error decoding schema parameter:', error)
      }
    }

    if (autoExecuteParam === 'true') {
      setShouldAutoExecute(true)
    }

    if (newTabParam === 'true') {
      setShouldCreateNewTab(true)
    }

    // Clear URL params after processing to avoid re-processing on rerenders
    if (queryParam || databaseParam || schemaParam || autoExecuteParam || newTabParam) {
      const newUrl = window.location.pathname
      window.history.replaceState({}, '', newUrl)
      setUrlParamsProcessed(true)
    }
  }, [location.search])

  // Define executeQuery function using useCallback
  const executeQuery = useCallback(async () => {
    const queryToExecute = sqlQuery.trim()
    
    console.log('Execute query called')
    console.log('SQL Query:', queryToExecute)
    console.log('Active Connection:', activeConnection)
    console.log('Selected Database:', selectedDatabase)
    
    if (!queryToExecute.trim()) {
      toast.error('Please enter a SQL query')
      return
    }

    if (!activeConnection) {
      toast.error('No database connection selected')
      return
    }

    if (!selectedDatabase) {
      toast.error('Please select a database to query')
      return
    }

    console.log('Executing query with connection:', {
      id: activeConnection.id,
      name: activeConnection.name,
      type: activeConnection.type,
      database: selectedDatabase
    })

    // Get the data source to validate it exists
    const dataSource = await fetchDataSourceDirect(activeConnection.id).catch((err: unknown) => {
      console.error('Error fetching data source:', err)
      return null
    })
    
    if (!dataSource) {
      console.log('Data source not found')
      toast.error('Data source not found. Please check your connection settings.')
      return
    }

    try {
      const queryRequest = {
        datasourceId: activeConnection.id,
        databaseName: selectedDatabase,
        query: queryToExecute.trim(),
        limit: 1000,
        timeout: 30,
      }
      console.log('Query request:', queryRequest)

      const result = await executeQueryMutation.mutateAsync(queryRequest)
      // console.log('Query result received:', result)

      // Transform API result to match UI expectations
      // Handle the AgentProxyExecuteQueryResponse type
      const resultData = result as AgentProxyExecuteQueryResponse;
      const extendedResult: ExtendedQueryResult = {
        columns: Array.isArray(resultData.columns) 
          ? resultData.columns.map((col: string | { name: string }) => {
              if (typeof col === 'string') return col;
              if (typeof col === 'object' && 'name' in col) {
                return col.name;
              }
              return String(col);
            })
          : [],
        rows: Array.isArray(resultData.rows) 
          ? resultData.rows.map((row: unknown[] | Record<string, unknown>) => Array.isArray(row) ? row : Object.values(row))
          : [],
        rowCount: Array.isArray(resultData.rows) ? resultData.rows.length : (resultData.totalRows || 0),
        executionTime: resultData.execution_time_ms ? resultData.execution_time_ms / 1000 : 0,
        executionTimeMs: resultData.execution_time_ms || 0,
        affectedRows: resultData.affected_rows || 0,
        query: queryToExecute,
        timestamp: new Date(),
        status: 'success' as const,
        message: resultData.message,
      }

      // console.log('Extended result created:', extendedResult)
      
      // Check if we're executing from a pending tab
      const currentTab = queryResults[activeResultTab]
      let newResults: ExtendedQueryResult[]
      
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (currentTab && currentTab.status === 'pending' && currentTab.query === queryToExecute) {
        // Update the existing pending tab
        newResults = Array.from(queryResults)
        newResults[activeResultTab] = {
          ...extendedResult,
          savedQueryId: currentTab.savedQueryId,
          savedQueryName: currentTab.savedQueryName,
        }
      } else {
        // Create a new tab for the result
        newResults = [extendedResult, ...queryResults.slice(0, 49)]
        setActiveResultTab(0)
      }
      
      setQueryResults(newResults)

      // Save results to localStorage (limit to 5 results per datasource)
      const persistedResults = newResults.slice(0, 50)
        .filter(result => result.status !== 'pending')
        .map(result => ({
          ...result,
          timestamp: result.timestamp.toISOString(),
          status: result.status as 'success' | 'error' | undefined
        }))
      saveQueryResults(
        activeConnection.id,
        persistedResults,
        queryToExecute,
        selectedDatabase,
        selectedSchema
      )

      // Refresh query history
      void refetchHistory()
    } catch (error: unknown) {
      console.error('Query execution error:', error)
      
      // Handle specific validation errors
      let errorMessage = 'Query execution failed'
      
      if (error && typeof error === 'object' && 'status' in error && error.status === 400) {
        if ('body' in error && error.body && typeof error.body === 'object' && 'message' in error.body) {
          const { body } = error as { body: { message: string } }
          errorMessage = `Validation Error: ${body.message}`
        } else if ('body' in error && error.body && typeof error.body === 'object' && 'errors' in error.body) {
          const { body } = error as { body: { errors: unknown } }
          const { errors } = body
          const validationErrors = Array.isArray(errors) 
            ? errors.join(', ')
            : JSON.stringify(errors)
          errorMessage = `Validation Error: ${validationErrors}`
        } else {
          errorMessage = 'Bad Request: Please check your query and connection settings'
        }
      } else if (error && typeof error === 'object' && 'status' in error && error.status === 401) {
        errorMessage = 'Authentication failed. Please check your login credentials.'
      } else if (error && typeof error === 'object' && 'status' in error && error.status === 404) {
        errorMessage = 'Data source or database not found. Please check your connection settings.'
      } else if (error && typeof error === 'object' && 'status' in error && error.status === 500) {
        if ('body' in error && error.body && typeof error.body === 'object' && 'message' in error.body) {
          const { body } = error as { body: { message: string } }
          errorMessage = body.message
        } else {
          errorMessage = 'Server error. Please try again later.'
        }
      } else if (error instanceof Error) {
        errorMessage = error.message
      }
      
      toast.error(errorMessage)
      
      // Still add failed result to history for debugging
      const failedResult: ExtendedQueryResult = {
        columns: [],
        rows: [],
        rowCount: 0,
        executionTime: 0,
        executionTimeMs: 0,
        status: 'error',
        message: errorMessage,
        query: queryToExecute,
        timestamp: new Date(),
        affectedRows: undefined,
      }
      
      // Check if we're executing from a pending tab
      const currentTab = queryResults[activeResultTab]
      let newResults: ExtendedQueryResult[]
      
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (currentTab && currentTab.status === 'pending' && currentTab.query === queryToExecute) {
        // Update the existing pending tab with error
        newResults = Array.from(queryResults)
        newResults[activeResultTab] = {
          ...failedResult,
          savedQueryId: currentTab.savedQueryId,
          savedQueryName: currentTab.savedQueryName,
        }
      } else {
        // Create a new tab for the error
        newResults = [failedResult, ...queryResults.slice(0, 49)]
        setActiveResultTab(0)
      }
      
      setQueryResults(newResults)
      setActiveResultTab(0)

      // Save error results to localStorage too
      const persistedResults = newResults.slice(0, 50)
        .filter(result => result.status !== 'pending')
        .map(result => ({
          ...result,
          timestamp: result.timestamp.toISOString(),
          status: result.status as 'success' | 'error' | undefined
        }))
      saveQueryResults(
        activeConnection.id,
        persistedResults,
        queryToExecute,
        selectedDatabase,
        selectedSchema
      )
    }
  }, [sqlQuery, activeConnection, selectedDatabase, executeQueryMutation, refetchHistory, activeResultTab, queryResults, selectedSchema])

  // Auto-execute query if requested via URL parameter
  useEffect(() => {
    if (shouldAutoExecute && sqlQuery.trim() && activeConnection && selectedDatabase) {
      // Small delay to ensure all states are properly set
      const timeoutId = setTimeout(() => {
        void executeQuery()
        setShouldAutoExecute(false)
      }, 500)
      
      return () => clearTimeout(timeoutId)
    }
    return undefined
  }, [shouldAutoExecute, sqlQuery, activeConnection, selectedDatabase, executeQuery])


  const formatActiveTabContent = useCallback((content: string) => {
    return format(content, {
      language: activeConnection?.type === 'postgresql' ? 'postgresql' : 'mysql',
    })
  }, [activeConnection?.type])

  const formatQuery = useCallback(() => {
    try {
      // Use the TabbedSqlEditor's formatActiveTab method to format the active tab
      tabbedSqlEditorRef.current?.formatActiveTab(formatActiveTabContent)
    } catch {
      toast.error('Failed to format query')
    }
  }, [formatActiveTabContent])

  // Stable callback for toggle functions using global store
  const handleToggleSavedQueries = useCallback(() => {
    setShowQueryLibrary(!showSavedQueries)
  }, [showSavedQueries, setShowQueryLibrary])

  const handleToggleHistory = useCallback(() => {
    setShowQueryHistory(!showHistory)
  }, [showHistory, setShowQueryHistory])

  const handleToggleAIAssistant = useCallback(() => {
    setShowAIAssistant(!showAIAssistant)
  }, [showAIAssistant, setShowAIAssistant])

  const handleExplainQuery = useCallback(() => {
    const explainAsync = async () => {
      const queryToExplain = getQueryToExecute()
      
      if (!queryToExplain.trim()) {
        toast.error('Please enter a SQL query or position cursor in a query block')
        return
      }

      if (!activeConnection) {
        toast.error('No database connection selected')
        return
      }

      if (!selectedDatabase) {
        toast.error('Please select a database to query')
        return
      }

      // Check if the query is a SELECT statement (API only supports SELECT queries for explain)
      // Strip comments first before checking if it's a SELECT query
      const cleanQuery = stripComments(queryToExplain)
      const trimmedQuery = cleanQuery.trim().toLowerCase()
      if (!trimmedQuery.startsWith('select')) {
        toast.error('Only SELECT queries can be explained')
        return
      }

      try {
        const result = await explainQueryMutation.mutateAsync({
          datasourceId: activeConnection.id,
          databaseName: selectedDatabase,
          query: queryToExplain.trim(),
        })

        // Type the result as an explain query response - matching AgentProxyExplainQueryPlanResponse
        interface ExplainQueryResponse {
          type?: string;
          explanation?: {
            columns?: string[];
            rows?: unknown[][];
            affected_rows?: number | null;
            execution_time?: number;
          };
          execution_time_ms?: number;
        }

        const explainData = result as ExplainQueryResponse;

        // Create a result to show the execution plan
        const explainResult: ExtendedQueryResult = {
          columns: explainData.explanation?.columns || ['Query Plan'],
          rows: explainData.explanation?.rows || [['No execution plan available']],
          rowCount: explainData.explanation?.rows?.length || 1,
          executionTime: explainData.execution_time_ms ? explainData.execution_time_ms / 1000 : (explainData.explanation?.execution_time || 0),
          executionTimeMs: explainData.execution_time_ms || (explainData.explanation?.execution_time ? explainData.explanation.execution_time * 1000 : 0),
          status: 'success',
          query: `EXPLAIN ${queryToExplain}`,
          timestamp: new Date(),
          affectedRows: explainData.explanation?.affected_rows ?? undefined,
        }

        const newResults = [explainResult, ...queryResults.slice(0, 49)]
        setQueryResults(newResults)
        setActiveResultTab(0)
        
        // Save explain results to localStorage
        const persistedResults = newResults.slice(0, 50)
          .filter(result => result.status !== 'pending')
          .map(result => ({
            ...result,
            timestamp: result.timestamp.toISOString(),
            status: result.status as 'success' | 'error' | undefined
          }))
        saveQueryResults(
          activeConnection.id,
          persistedResults,
          `EXPLAIN ${queryToExplain}`,
          selectedDatabase,
          selectedSchema
        )
        
      } catch (error: unknown) {
        console.error('Query explain error:', error)
        const errorMessage = 
          (error && typeof error === 'object' && 'body' in error && error.body && 
           typeof error.body === 'object' && 'message' in error.body ? (error.body as { message: string }).message : null) ||
          (error instanceof Error ? error.message : null) ||
          'Failed to explain query'
        toast.error(errorMessage)
      }
    }
    
    void explainAsync()
  }, [getQueryToExecute, activeConnection, selectedDatabase, explainQueryMutation, queryResults, selectedSchema])

  const handleSaveTab = useCallback(async (tab: EditorTab) => {
    
    if (!activeConnection) {
      toast.error('No database connection selected')
      return
    }

    // Don't allow saving empty queries
    if (!tab.content || tab.content.trim().length === 0) {
      toast.error('Cannot save empty query')
      return
    }

    // If tab has a saved query ID, update the existing query
    if (tab.savedQueryId && tab.savedQueryCollectionId) {
      try {
        await useCollectionsStore.getState().updateQuery(
          tab.savedQueryCollectionId,
          tab.savedQueryId as unknown as number, // Cast to bypass type check since API spec uses number but actual IDs are string UUIDs
          {
            query: tab.content.trim(),
            databaseName: selectedDatabase,
            schemaName: activeConnection.type === 'postgresql' ? selectedSchema : undefined,
          }
        )
        toast.success(`Updated "${tab.savedQueryName || 'Query'}"`)
        
        // Dispatch event to notify all CollectionTree components
        dispatchCollectionEvent(COLLECTION_EVENTS.QUERY_UPDATED, {
          collectionId: tab.savedQueryCollectionId,
          queryId: tab.savedQueryId
        })
        
        // Update the tab's database/schema properties
        const updatedTab = {
          ...tab,
          databaseName: selectedDatabase,
          schemaName: activeConnection.type === 'postgresql' ? selectedSchema : undefined
        }
        const updatedTabs = currentTabs.map(t => 
          t.id === tab.id ? updatedTab : t
        )
        setCurrentTabs(updatedTabs)
        saveTabs(activeConnection.id, updatedTabs, activeTabId)
        
        // Mark the tab as clean in the editor component
        tabbedSqlEditorRef.current?.markTabClean(tab.id)
      } catch (error: unknown) {
        console.error('Update query error:', error)
        
        // If the query doesn't exist anymore (404 or similar), treat as new query
        if ((error && typeof error === 'object' && 'status' in error && error.status === 404) || 
            (error instanceof Error && error.message.includes('Not Found'))) {
          console.log('[QueryEditor] Saved query no longer exists, treating as new query')
          
          // Clear saved query metadata from the actual tab
          const clearedTab = {
            ...tab,
            savedQueryId: undefined,
            savedQueryCollectionId: undefined,
            savedQueryName: undefined,
            label: 'Query 1' // Reset to default label
          }
          
          // Update the tab state to clear saved query metadata
          const updatedTabs = currentTabs.map(t => 
            t.id === tab.id ? clearedTab : t
          )
          setCurrentTabs(updatedTabs)
          
          // Update localStorage as well
          saveTabs(activeConnection.id, updatedTabs, activeTabId)
          
          // Show save dialog for new query
          setEditingTab(clearedTab)
          setCollectionManagerMode('create-query')
          setCollectionManagerOpen(true)
          return // Don't re-throw the error, let user save as new query
        }
        
        toast.error((error instanceof Error ? error.message : null) || 'Failed to update query')
        throw error // Re-throw other errors to propagate to TabbedSqlEditor
      }
    } else {
      // New query - show save dialog with current database/schema
      setEditingTab({
        ...tab,
        databaseName: selectedDatabase,
        schemaName: activeConnection.type === 'postgresql' ? selectedSchema : undefined
      })
      setCollectionManagerMode('create-query')
      setCollectionManagerOpen(true)
      // Don't throw error - the CollectionManager will dispatch QUERY_CREATED event with tabId
    }
  }, [activeConnection, selectedDatabase, selectedSchema, currentTabs, activeTabId, tabbedSqlEditorRef])

  const handleSaveCurrentTab = useCallback(() => {
    // Get the current active tab from current state (not localStorage)
    const currentActiveTab = currentTabs.find(tab => tab.id === activeTabId)
    
    if (currentActiveTab) {
      void handleSaveTab(currentActiveTab)
    } else {
      toast.error('No active query tab to save')
    }
  }, [currentTabs, activeTabId, handleSaveTab])

  const handleExecuteAll = useCallback(() => {
    // Execute the entire tab content as one bulk request
    void executeQuery()
  }, [executeQuery])

  const handleCloseSavedQueries = useCallback(() => {
    setShowQueryLibrary(false)
  }, [setShowQueryLibrary])

  const handleCloseCollectionManager = useCallback(() => {
    setCollectionManagerOpen(false)
    setEditingTab(null)
    // Refresh collections and saved queries after saving
    void fetchCollections()
  }, [fetchCollections])

  const handleCloseHistory = useCallback(() => {
    setShowQueryHistory(false)
  }, [setShowQueryHistory])

  const handleResultTabClick = useCallback((index: number, query: string) => {
    setActiveResultTab(index)
    // Add query to a new tab instead of replacing current tab content
    if (tabbedSqlEditorRef.current) {
      tabbedSqlEditorRef.current.addTab(query, `History Query ${index + 1}`)
    }
  }, [])

  const handleCreateQuery = useCallback(() => {
    setCollectionManagerOpen(true)
  }, [])

  const handleFetchNextPage = useCallback(() => {
    databasesQuery.fetchNextPage()
  }, [databasesQuery])

  const handleFetchNextSchemaPage = useCallback(() => {
    schemasQuery.fetchNextPage()
  }, [schemasQuery])

  const handleTabsChange = useCallback((tabs: EditorTab[], activeTabId: string) => {
    // Enhanced tab change handler with saved query info preservation
    if (activeTabId) {
      // Save tabs directly - they already contain saved query metadata
      saveTabs(activeConnection?.id || '', tabs, activeTabId);
    }
    // Update current tabs state for save button
    setCurrentTabs(tabs);
    setActiveTabId(activeTabId);
  }, [activeConnection?.id])

  const handleTabSelect = useCallback((tab: EditorTab) => {
    // When switching to a saved query tab, update the database and schema dropdowns
    if (tab.databaseName && activeConnection) {
      const isDbAvailable = databasesQuery.data.some(db => db.name === tab.databaseName)
      if (isDbAvailable && tab.databaseName !== selectedDatabase) {
        console.log('[Tab Switch] Restoring database from saved query:', tab.databaseName)
        setSelectedDatabase(tab.databaseName)
        
        // If there's a schema to restore, set it as pending (schemas will load after database change)
        if (tab.schemaName && activeConnection.type === 'postgresql') {
          console.log('[Tab Switch] Setting pending schema restoration:', tab.schemaName)
          setPendingSchemaRestore(tab.schemaName)
        }
      } else if (!isDbAvailable && tab.savedQueryId) {
        // Only show error for saved queries, not for regular tabs
        console.log('[Tab Switch] Database not available:', tab.databaseName)
      } else {
        // Database is already selected, handle schema restoration directly
        if (tab.schemaName && activeConnection.type === 'postgresql') {
          if (schemasQuery.data.length > 0) {
            const isSchemaAvailable = schemasQuery.data.some(schema => schema.name === tab.schemaName)
            if (isSchemaAvailable && tab.schemaName !== selectedSchema) {
              console.log('[Tab Switch] Restoring schema from saved query:', tab.schemaName)
              setSelectedSchema(tab.schemaName)
              setHasUserSelectedSchema(true)
            } else if (!isSchemaAvailable && tab.savedQueryId) {
              // Only show error for saved queries
              console.log('[Tab Switch] Schema not available:', tab.schemaName)
            }
          } else {
            // Schemas not loaded yet, set as pending
            console.log('[Tab Switch] Setting pending schema restoration:', tab.schemaName)
            setPendingSchemaRestore(tab.schemaName)
          }
        }
      }
    } else if (tab.schemaName && activeConnection?.type === 'postgresql' && !tab.databaseName) {
      // Handle schema-only restoration for current database
      if (schemasQuery.data.length > 0) {
        const isSchemaAvailable = schemasQuery.data.some(schema => schema.name === tab.schemaName)
        if (isSchemaAvailable && tab.schemaName !== selectedSchema) {
          console.log('[Tab Switch] Restoring schema from saved query:', tab.schemaName)
          setSelectedSchema(tab.schemaName)
          setHasUserSelectedSchema(true)
        }
      } else {
        // Schemas not loaded yet, set as pending
        console.log('[Tab Switch] Setting pending schema restoration:', tab.schemaName)
        setPendingSchemaRestore(tab.schemaName)
      }
    }
  }, [activeConnection, databasesQuery.data, selectedDatabase, selectedSchema, schemasQuery.data])

  const handleOnExecute = useCallback((query: string, type?: 'block' | 'all') => {
    if (type === 'block') {
      // Execute single query block
      void executeSpecificQuery(query)
    } else {
      // Execute all queries in the current tab as one bulk request
      if (!query.trim()) {
        toast.error('Please enter a SQL query')
        return
      }
      
      // Send entire query content as one request to the agent proxy
      void executeSpecificQuery(query)
    }
  }, [executeSpecificQuery])

  const handleActiveQueryChange = useCallback((query: string) => {
    setSqlQuery(query)
    // Clear queryFromUrl after user starts editing
    if (queryFromUrl) {
      setQueryFromUrl(null)
    }
  }, [queryFromUrl])

  const handleNewTabCreated = useCallback(() => {
    setShouldCreateNewTab(false)
    setPendingQueryLoad(null)
    setEditingTab(null)
  }, [])


  const loadCollectionQuery = useCallback(async (query: CollectionQuery & { collectionId: string }) => {
    console.log('loadCollectionQuery called with:', query);
    
    // Always fetch fresh query data to avoid stale cache
    // Try to fetch fresh query data
    // Note: The API returns queries differently based on whether they're in folders or not
    // For queries in folders, we might not get fresh data from the collection endpoint
    // This is a known limitation - we'll use the data we have
    if (query.folderId) {
      console.log('Query is in a folder, using provided data:', query);
    } else {
      // For root queries, try to get fresh data
      try {
        const { CollectionsService } = await import('@/services/api/services/CollectionsService');
        const freshCollection = await CollectionsService.getCollection({ 
          collectionId: query.collectionId 
        });
        
        // Find the fresh query data in root queries
        const freshQuery = freshCollection.queries?.find(q => q.id === query.id);
        
        if (freshQuery) {
          // Use fresh data instead of potentially stale data
          query = { ...freshQuery, collectionId: query.collectionId };
          console.log('Using fresh query data:', query);
        } else {
          // This shouldn't happen for root queries, but handle gracefully
          console.warn('Root query not found in fresh collection data, using provided data:', query);
        }
      } catch (error) {
        console.error('Failed to fetch fresh query data:', error);
        // Continue with potentially stale data if fetch fails
      }
    }
    
    if (query.query && activeConnection) {
      // Check if a tab with this collection query already exists
      const storedState = loadQueryState(activeConnection.id)
      const existingTabIndex = storedState?.tabs?.findIndex(tab => 
        tab.savedQueryId === query.id && 
        tab.savedQueryCollectionId === query.collectionId &&
        tab.savedQueryName === query.name
      )
      
      if (existingTabIndex !== undefined && existingTabIndex >= 0 && storedState?.tabs) {
        // Tab already exists - update its content and switch to it
        console.log('Found existing tab for collection query, updating content');
        const existingTab = storedState.tabs[existingTabIndex]
        const updatedTab = {
          ...existingTab,
          content: query.query,
          label: query.name || 'Query',
          savedQueryId: query.id,
          savedQueryCollectionId: query.collectionId,
          savedQueryName: query.name,
          databaseName: query.databaseName || undefined,
          schemaName: query.schemaName || undefined,
          isDirty: false // Mark as clean since it matches saved content
        }
        
        const updatedTabs = Array.from(storedState.tabs)
        updatedTabs[existingTabIndex] = updatedTab
        
        // Save updated tabs and switch to the existing tab
        saveTabs(activeConnection.id, updatedTabs, existingTab.id)
        setSqlQuery(query.query)
        
        // Trigger the TabbedSqlEditor to switch to the existing tab
        setShouldCreateNewTab(true)
        setEditingTab({
          id: existingTab.id,
          label: query.name || 'Query',
          content: query.query,
          savedQueryId: query.id,
          savedQueryCollectionId: query.collectionId,
          savedQueryName: query.name,
          databaseName: query.databaseName || undefined,
          schemaName: query.schemaName || undefined,
          isDirty: false
        })
        
        // Switch to existing tab in results if it exists
        const existingResultIndex = queryResults.findIndex(result => 
          result.savedQueryId === query.id
        )
        if (existingResultIndex >= 0) {
          setActiveResultTab(existingResultIndex)
        }
        
        // Trigger dropdown sync for the existing tab
        handleTabSelect(updatedTab)
        
        return; // Early return - don't create new tab
      }
      
      // No existing tab found - create new one
      setSqlQuery(query.query)
      setPendingQueryLoad(query.query)
      setShouldCreateNewTab(true)
      
      // Store the saved query info to be added to the new tab with proper type mapping
      setEditingTab({
        id: '', // Will be set by TabbedSqlEditor
        label: query.name || 'Query',
        content: query.query,
        savedQueryId: query.id,
        savedQueryCollectionId: query.collectionId,
        savedQueryName: query.name,
        databaseName: query.databaseName || undefined,
        schemaName: query.schemaName || undefined,
        isDirty: false
      })
      
      // Create a new result tab for tracking execution with collection query info
      const newQueryTab: ExtendedQueryResult = {
        columns: [],
        rows: [],
        rowCount: 0,
        executionTime: 0,
        status: 'pending' as const,
        message: `Collection Query: ${query.name}`,
        query: query.query,
        timestamp: new Date(),
        savedQueryId: query.id,
        savedQueryName: query.name,
        databaseContext: {
          database: query.databaseName || selectedDatabase,
          schema: query.schemaName || selectedSchema,
        }
      }
      
      // Add the new tab to the beginning of results
      const newResults = [newQueryTab, ...queryResults.slice(0, 49)]
      console.log('Creating new collection query tab, newResults:', newResults);
      setQueryResults(newResults)
      setActiveResultTab(0)
      
      // Restore database selection if saved and available
      if (query.databaseName) {
        const isDbAvailable = databasesQuery.data.some(db => db.name === query.databaseName)
        if (isDbAvailable && query.databaseName !== selectedDatabase) {
          console.log('Restoring database from collection query:', query.databaseName)
          setSelectedDatabase(query.databaseName)
          
          // If there's a schema to restore, set it as pending (schemas will load after database change)
          if (query.schemaName && activeConnection.type === 'postgresql') {
            console.log('Setting pending schema restoration:', query.schemaName)
            setPendingSchemaRestore(query.schemaName)
          }
        } else if (!isDbAvailable) {
          toast.error(`Database "${query.databaseName}" from saved query is not available in current connection`)
        } else {
          // Database is already selected, handle schema restoration directly
          if (query.schemaName && activeConnection.type === 'postgresql') {
            if (schemasQuery.data.length > 0) {
              const isSchemaAvailable = schemasQuery.data.some(schema => schema.name === query.schemaName)
              if (isSchemaAvailable && query.schemaName !== selectedSchema) {
                console.log('Restoring schema from saved query:', query.schemaName)
                setSelectedSchema(query.schemaName)
                setHasUserSelectedSchema(true)
              } else if (!isSchemaAvailable) {
                toast.error(`Schema "${query.schemaName}" from saved query is not available in current database`)
              }
            } else {
              // Schemas not loaded yet, set as pending
              console.log('Setting pending schema restoration:', query.schemaName)
              setPendingSchemaRestore(query.schemaName)
            }
          }
        }
      } else {
        // No database in saved query, handle schema restoration for current database
        if (query.schemaName && activeConnection.type === 'postgresql') {
          if (schemasQuery.data.length > 0) {
            const isSchemaAvailable = schemasQuery.data.some(schema => schema.name === query.schemaName)
            if (isSchemaAvailable && query.schemaName !== selectedSchema) {
              console.log('Restoring schema from saved query:', query.schemaName)
              setSelectedSchema(query.schemaName)
              setHasUserSelectedSchema(true)
            } else if (!isSchemaAvailable) {
              toast.error(`Schema "${query.schemaName}" from saved query is not available in current database`)
            }
          } else {
            // Schemas not loaded yet, set as pending
            console.log('Setting pending schema restoration:', query.schemaName)
            setPendingSchemaRestore(query.schemaName)
          }
        }
      }
    }
  }, [activeConnection, databasesQuery.data, queryResults, selectedDatabase, selectedSchema, schemasQuery.data, handleTabSelect])

  const exportResults = useCallback(() => {
    if (queryResults.length === 0) {
      toast.error('No results to export')
      return
    }

    const result = queryResults[activeResultTab]
    if (!result.rows.length) {
      toast.error('No data to export')
      return
    }

    try {
      // Convert to CSV
      const csvContent = [
        result.columns.join(','),
        ...result.rows.map((row: unknown[]) => 
          row.map((cell: unknown) => 
            typeof cell === 'string' && cell.includes(',') ? `"${cell}"` : cell
          ).join(',')
        )
      ].join('\n')

      const blob = new Blob([csvContent], { type: 'text/csv' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `query_results_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast.success('Results exported to CSV')
    } catch (error) {
      console.error('Export error:', error)
      toast.error('Failed to export results')
    }
  }, [queryResults, activeResultTab])

  useEffect(() => {
    // Only refetch if we have an active connection and no data yet
    if (activeConnection && databasesQuery.data.length === 0 && !databasesQuery.isLoading) {
      console.log('Triggering initial database fetch for connection:', activeConnection.name)
      void databasesQuery.refetch()
    }
  }, [activeConnection, databasesQuery])

  if (!activeConnection) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
            No Connection Selected
          </h2>
          <p className="text-gray-600 dark:text-gray-400">
            Please select a database connection to start querying.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full w-full overflow-hidden relative">
      <div className="h-full w-full pr-12">
        <Allotment 
          defaultSizes={allotmentMainSizes}
          onChange={handlePanelResize}
        >
        {/* Main Content */}
        <Allotment.Pane minSize={400} className="min-w-0">
          <Allotment vertical defaultSizes={allotmentVerticalSizes}>
            {/* Query Editor */}
            <Allotment.Pane minSize={250} preferredSize={400}>
            <div className="h-full w-full bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden min-w-0">
              {/* Toolbar */}
              <div className="flex items-center px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex-shrink-0 relative bg-white dark:bg-gray-800 min-w-0 gap-3">
                <div className="flex items-center space-x-3 flex-shrink min-w-0 max-w-[60%]">
                  
                  {/* Database Selection */}
                  <div className="flex items-center space-x-1.5 min-w-0">
                    <label className="text-[11px] font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                      DB:
                    </label>
                    <div className={`${getDynamicDatabaseWidth} max-w-40`}>
                      <PaginatedSelect
                        value={selectedDatabase}
                        onChange={setSelectedDatabase}
                        placeholder={`Select (${filteredDatabases.length})`}
                        isLoading={databasesQuery.isLoading}
                        isFetchingNextPage={databasesQuery.isFetchingNextPage}
                        hasNextPage={databasesQuery.hasNextPage}
                        onFetchNextPage={handleFetchNextPage}
                        options={filteredDatabases}
                        getOptionValue={getDatabaseOptionValue}
                        getOptionLabel={getDatabaseOptionLabel}
                        disabled={databasesQuery.isLoading}
                        className="text-[11px]"
                        loadingText="Loading..."
                      />
                    </div>
                  </div>

                  {/* Schema Selection (PostgreSQL only) */}
                  {activeConnection.type === 'postgresql' && selectedDatabase && (
                    <div className="flex items-center space-x-1.5 min-w-0">
                      <label className="text-[11px] font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
                        Sch:
                      </label>
                      <div className={`${getDynamicSchemaWidth} max-w-40`}>
                        <PaginatedSelect
                          value={selectedSchema}
                          onChange={handleSchemaChange}
                          placeholder={`Select (${filteredSchemas.length})`}
                          isLoading={schemasQuery.isLoading}
                          isFetchingNextPage={schemasQuery.isFetchingNextPage}
                          hasNextPage={schemasQuery.hasNextPage}
                          onFetchNextPage={handleFetchNextSchemaPage}
                          options={filteredSchemas}
                          getOptionValue={getSchemaOptionValue}
                          getOptionLabel={getSchemaOptionLabel}
                          disabled={schemasQuery.isLoading}
                          className="text-[11px]"
                          loadingText="Loading..."
                        />
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex items-center space-x-1 ml-auto flex-shrink-0">
                  
                  {/* Save button - always visible, compact on small screens */}
                  <button type="button"
                    onClick={handleSaveCurrentTab}
                    className="btn-secondary btn-sm flex items-center space-x-1"
                    title="Save query (Ctrl+S)"
                  >
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17 3H5C3.89 3 3 3.9 3 5V19C3 20.1 3.89 21 5 21H19C20.1 21 21 20.1 21 19V7L17 3M19 19H5V5H16.17L19 7.83V19M12 12C10.34 12 9 13.34 9 15S10.34 18 12 18 15 16.66 15 15 13.66 12 12 12M6 6H15V10H6V6Z" />
                    </svg>
                    <span className="hidden sm:inline">Save</span>
                  </button>
                  
                  {/* Execute button - always visible, compact on small screens */}
                  <button type="button"
                    onClick={handleExecuteAll}
                    disabled={executeQueryMutation.isPending}
                    className="btn-primary btn-sm flex items-center space-x-1"
                    title="Execute all queries as one bulk request"
                  >
                    <PlayIcon className="w-4 h-4" />
                    <span className="hidden sm:inline">
                      {executeQueryMutation.isPending 
                        ? 'Executing...' 
                        : 'Execute'
                      }
                    </span>
                    <span className="sm:hidden">
                      {executeQueryMutation.isPending 
                        ? '...' 
                        : 'Run'
                      }
                    </span>
                  </button>
                </div>
              </div>

              {/* SQL Editor */}
              <div className="flex-1 min-h-0 min-w-0 overflow-hidden">
                <TabbedSqlEditor
                  ref={tabbedSqlEditorRef}
                  onExecute={handleOnExecute}
                  onActiveQueryChange={handleActiveQueryChange}
                  onTabsChange={handleTabsChange}
                  onTabSelect={handleTabSelect}
                  initialContent={pendingQueryLoad || queryFromUrl || sqlQuery}
                  datasourceId={activeConnection.id}
                  databaseName={selectedDatabase}
                  schemaName={selectedSchema}
                  databaseType={activeConnection.type}
                  shouldCreateNewTab={shouldCreateNewTab}
                  onNewTabCreated={handleNewTabCreated}
                  onSaveQuery={handleSaveTab}
                  savedQueryInfo={savedQueryInfo}
                  className="h-full"
                />
              </div>
            </div>
          </Allotment.Pane>

          {/* Query Results */}
          <Allotment.Pane>
            <div className="h-full bg-white dark:bg-gray-800 flex flex-col">
              {/* Results Content */}
              <div className="flex-1 min-h-0 overflow-hidden">
                {!selectedDatabase ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2.21 1.79 4 4 4h8c2.21 0 4-1.79 4-4V7c0-2.21-1.79-4-4-4H8c-2.21 0-4 1.79-4 4z" />
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        Select a Database
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Choose a database from the dropdown above to start querying.
                      </p>
                    </div>
                  </div>
                ) : queryResults.length === 0 ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="text-center">
                      <PlayIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No Query Results
                      </h3>
                      <p className="text-gray-600 dark:text-gray-400">
                        Execute a SQL query to see results here.
                      </p>
                    </div>
                  </div>
                ) : (
                  <QueryResults
                    result={queryResults[activeResultTab]}
                    isLoading={executeQueryMutation.isPending}
                    databaseContext={databaseContext}
                    onExport={exportResults}
                  />
                )}
              </div>
            </div>
          </Allotment.Pane>
          </Allotment>
        </Allotment.Pane>

        {/* Right Sidebar - Query History, Saved Queries, or AI Assistant */}
        <Allotment.Pane 
          minSize={rightSidebarMinSize} 
          maxSize={rightSidebarMaxSize}
          preferredSize={Math.max(280, rightSidebarMinSize)}
          visible={showHistory || showSavedQueries || showAIAssistant}
        >
          {(showHistory || showSavedQueries || showAIAssistant) && (
            <>
              {showHistory ? (
              /* Query History Sidebar */
              <div className="h-full border-l border-gray-200 dark:border-gray-700">
                <div className="h-full bg-white dark:bg-gray-800 flex flex-col">
                  {/* Header */}
                  <div className="flex items-center justify-between p-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center space-x-2">
                      <ClockIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        Query History
                      </h3>
                    </div>
                    <div className="flex items-center space-x-1">
                      {queryResults.length > 0 && (
                        <button type="button"
                          onClick={() => {
                            setQueryResults([])
                            setActiveResultTab(0)
                            if (activeConnection) {
                              // Clear from localStorage too
                              const existing = loadQueryState(activeConnection.id)
                              if (existing) {
                                saveQueryState(
                                  activeConnection.id,
                                  existing.sqlQuery,
                                  existing.selectedDatabase,
                                  existing.selectedSchema,
                                  []
                                )
                              }
                            }
                          }}
                          className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                          title="Clear history"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                      <button type="button"
                        onClick={handleCloseHistory}
                        className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                        title="Close"
                      >
                        <XMarkIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Recent Queries */}
                  <div className="flex-1 overflow-auto p-4">
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Recent Queries</h4>
                        {queryResults.length > 0 && (
                          <span className="text-xs text-gray-500 dark:text-gray-400">
                            {queryResults.length} {queryResults.length === 1 ? 'query' : 'queries'}
                          </span>
                        )}
                      </div>
                      {queryResults.length === 0 ? (
                        <div className="text-center text-gray-500 dark:text-gray-400 py-8">
                          <ClockIcon className="w-8 h-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No recent queries</p>
                          <p className="text-xs mt-1">Execute queries to see them here</p>
                        </div>
                      ) : (
                        <>
                          {/* Quick Filter */}
                          <div className="flex items-center space-x-2 mb-3">
                            <button 
                              onClick={() => { setHistoryFilter('all') }}
                              className={`text-xs px-2 py-1 rounded transition-colors ${
                                historyFilter === 'all'
                                  ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`} 
                              type="button"
                            >
                              All
                            </button>
                            <button 
                              onClick={() => { setHistoryFilter('success') }}
                              className={`text-xs px-2 py-1 rounded transition-colors ${
                                historyFilter === 'success'
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300'
                                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                              type="button"
                            >
                              Success
                            </button>
                            <button 
                              onClick={() => { setHistoryFilter('error') }}
                              className={`text-xs px-2 py-1 rounded transition-colors ${
                                historyFilter === 'error'
                                  ? 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300'
                                  : 'text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700'
                              }`}
                              type="button"
                            >
                              Errors
                            </button>
                          </div>
                          
                          {queryResults
                            .slice(0, 50)
                            .map((result, originalIndex) => ({ result, originalIndex }))
                            .filter(({ result }) => {
                              if (historyFilter === 'all') return true
                              if (historyFilter === 'success') return result.status === 'success'
                              if (historyFilter === 'error') return result.status === 'error'
                              return true
                            })
                            .map(({ result, originalIndex }) => (
                            <div
                              key={originalIndex}
                              className={`p-3 border rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors group ${
                                activeResultTab === originalIndex ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' : 'border-gray-200 dark:border-gray-600'
                              }`}
                              onClick={() => { handleResultTabClick(originalIndex, result.query) }}
                            >
                              <div className="flex items-center justify-between mb-2">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  result.status === 'error' 
                                    ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                                    : result.status === 'pending'
                                    ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                                    : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                }`}>
                                  {result.status === 'error' ? 'ERROR' : result.status === 'pending' ? 'PENDING' : 'SUCCESS'}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-gray-400">
                                  {result.timestamp.toLocaleTimeString()}
                                </span>
                              </div>
                              {result.savedQueryName && (
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                  {result.savedQueryName}
                                </div>
                              )}
                              <div className="bg-gray-50 dark:bg-gray-700 p-2 rounded text-xs mb-2">
                                <p className="text-gray-900 dark:text-white font-mono">
                                  {result.query.length > 100 ? `${result.query.substring(0, 100)}...` : result.query}
                                </p>
                              </div>
                              <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                                <span>{result.rows?.length || result.rowCount || 0} rows</span>
                                <span>
                                  {formatExecutionTime(result.executionTimeMs, result.executionTime)}
                                </span>
                              </div>
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : showAIAssistant ? (
              /* AI Assistant Sidebar */
              <AIAssistant
                isOpen={showAIAssistant}
                onClose={() => { setShowAIAssistant(false) }}
                onExecuteQuery={(query) => {
                  // Add a new tab with the AI-generated query
                  tabbedSqlEditorRef.current?.addTab(query, 'AI Query')
                  
                  // Small delay to ensure the new tab is active, then execute the specific AI query
                  setTimeout(() => {
                    void executeSpecificQuery(query)
                  }, 100)
                }}
                selectedDatabase={selectedDatabase}
                selectedSchema={selectedSchema}
                connectionType={activeConnection?.type}
              />
            ) : (
              /* Saved Queries Sidebar with Collections */
              <div className="h-full border-l border-gray-200 dark:border-gray-700">
                <SavedQueriesWithCollections
                  onCollectionQuerySelect={loadCollectionQuery}
                  onCreateQuery={handleCreateQuery}
                  selectedDatabase={selectedDatabase}
                  selectedSchema={selectedSchema}
                  connectionType={activeConnection.type}
                  onClose={handleCloseSavedQueries}
                />
              </div>
            )}
            </>
          )}
        </Allotment.Pane>


        </Allotment>
      </div>

      {/* Vertical Toolbar - Always visible on the right */}
      <div className="absolute right-0 top-0 bottom-0 z-10">
        <VerticalToolbar 
          showQueryLibrary={showSavedQueries}
          showQueryHistory={showHistory}
          showAIAssistant={showAIAssistant}
          onToggleQueryLibrary={handleToggleSavedQueries}
          onToggleQueryHistory={handleToggleHistory}
          onToggleAIAssistant={handleToggleAIAssistant}
          onFormatQuery={formatQuery}
          onExplainQuery={handleExplainQuery}
          isExplainLoading={explainQueryMutation.isPending}
        />
      </div>

      {/* Collection Manager Modal */}
      {collectionManagerOpen && (
        <CollectionManager
          mode={collectionManagerMode}
          isOpen={collectionManagerOpen}
          onClose={handleCloseCollectionManager}
          initialQuery={initialQuery}
          tabId={editingTab?.id}
        />
      )}
    </div>
  )
}
